import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Avatar from "../../components/inputs/Avatar";
import ProgressBar from "../../components/ProgressBar";
import CongratsCertificateModal from "../Modals/CongratsCertificateModal";
import LearningPathCardImage from "./LearningPathCardImage";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

const LearningPathdetailsCard = ({
  course,
  courseStatus,
  state,
  courseStatusClassName,
}) => {
  const { t } = useTranslation("learningPaths");
  const navigate = useNavigate();
  const coursePath = `/employee/course/${course?.id}`;
  const menuId = `course-actions-${course?.id}`;

  const displayCourseVideoDuration = (videoDuration) => {
    let hours = parseInt(videoDuration / 60 / 60);
    let minutes = parseInt(videoDuration / 60 - hours * 60);
    return (
      <p className="font_14 font-color-grey ms-1 mb-0">
        {`${hours} ${t("learningPaths:hour")}: ${minutes} ${t(
          "learningPaths:minutes",
        )}`}
      </p>
    );
  };

  const handleDownloadCertificate = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (course?.enrollment?.certificate_url) {
      window.open(course.enrollment.certificate_url, "_blank");
    }
  };

  const handleShareCourse = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}${coursePath}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: course?.title,
          text: t("learningPaths:shareCourse"),
          url,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success(t("learningPaths:courseLinkCopied"));
      } else {
        window.prompt("Copy this link:", url);
      }
    } catch (error) {
      // user dismissed native share
    }
  };

  const handleWatchAgain = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(coursePath, { state });
  };

  const getCourseStatusAndCTA = () => {
    const progress = course?.enrollment?.progress || 0;

    if (progress === 0) {
      return {
        status: "not-started",
        statusText: t("learningPaths:notStarted") || "Not Started",
        ctaText: t("learningPaths:startCourse") || "Start Course",
        ctaIcon: "fas fa-play",
        buttonClass: "btn-course not-started-course",
        showProgress: false,
      };
    } else if (progress < 25) {
      return {
        status: "just-started",
        statusText: t("learningPaths:justStarted") || "Just Started",
        ctaText: t("learningPaths:continueCourse") || "Continue Course",
        ctaIcon: "fas fa-play",
        buttonClass: "btn-course in-progress-course",
        showProgress: true,
      };
    } else if (progress < 50) {
      return {
        status: "in-progress",
        statusText: t("learningPaths:inProgress") || "In Progress",
        ctaText: t("learningPaths:continueCourse") || "Continue Course",
        ctaIcon: "fas fa-play",
        buttonClass: "btn-course in-progress-course",
        showProgress: true,
      };
    } else if (progress < 75) {
      return {
        status: "halfway",
        statusText: t("learningPaths:halfway") || "Halfway There",
        ctaText: t("learningPaths:continueCourse") || "Continue Course",
        ctaIcon: "fas fa-play",
        buttonClass: "btn-course in-progress-course",
        showProgress: true,
      };
    } else if (progress < 100) {
      return {
        status: "almost-fdone",
        statusText: t("learningPaths:almostDone") || "Almost Done",
        ctaText: t("learningPaths:finishCourse") || "Finish Course",
        ctaIcon: "fas fa-play",
        buttonClass: "btn-course almost-completed-course",
        showProgress: true,
      };
    } else {
      return {
        status: "completed",
        statusText: t("learningPaths:completed") || "Completed",
        ctaText: t("learningPaths:viewCertificate") || "View Certificate",
        ctaIcon: "fas fa-certificate",
        buttonClass: "btn-course completed-course",
        showProgress: true,
      };
    }
  };

  const courseStatusInfo = getCourseStatusAndCTA();

  return (
    <div className="card-style learinig-path-card learinig-path-details-card">
      <CongratsCertificateModal
        CertificateUrl={course?.enrollment?.certificate_url}
      />
      <LearningPathCardImage
        src={course?.image_url}
        alt={course?.title || ""}
      />

      <div className="learinig-path-card__content">
        {course?.topics?.length && (
          <span
            className="course-label mb-1"
            style={{ backgroundColor: `${course?.topics[0]?.color}` }}
          >
            {course?.topics[0]?.title}
          </span>
        )}
        <h2 className="card-title mb-1">{course?.title}</h2>

        <div className="course-status mb-2">
          <span className={`status-badge ${courseStatusInfo.status}`}>
            {courseStatusInfo.statusText}
          </span>
        </div>

        {courseStatusInfo.showProgress && (
          <ProgressBar
            width="280px"
            percentageText={
              course?.enrollment?.progress
                ? `${course?.enrollment?.progress}%`
                : "0%"
            }
          />
        )}
        <ul className="list-details">
          <li>
            {" "}
            <Avatar
              width="24"
              height="24"
              avatarUrl={course?.tutor?.avatar_url}
            />
            <p className="font_14 font-color-grey ms-1 mb-0">
              {course?.tutor?.name}
            </p>
          </li>
          <li>
            <span className="fas fa-play-circle fa-fw font-color-grey"></span>
            <p className="font_14 font-color-grey ms-1 mb-0">
              {course?.lessons_count + " " + t("learningPaths:lesson")}
            </p>
          </li>
          <li>
            <span className="fas fa-clock fa-fw font-color-grey"></span>
            {displayCourseVideoDuration(course?.video_duration)}
          </li>
        </ul>
      </div>

      <div className="learinig-path-card__btn">
        {courseStatusInfo.status === "completed" ? (
          <div className="learinig-path-details-card__certificate-actions">
            <button
              type="button"
              className="btn btn-course completed-course"
              onClick={handleDownloadCertificate}
              disabled={!course?.enrollment?.certificate_url}
            >
              <span className="fas fa-download icon fa-fw"></span>
              <div className="flex-auto mb-0">
                {t("learningPaths:downloadCertificate")}
              </div>
            </button>
            <div className="dots-tools learinig-path-details-card__actions-menu">
              <div className="dropdown tools">
                <button
                  type="button"
                  className="btn learinig-path-details-card__menu-btn"
                  id={menuId}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label={t("learningPaths:courseQuickActions")}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="fas fa-ellipsis-h fa-fw" aria-hidden="true"></span>
                </button>
                <ul
                  className="dropdown-menu dropdown-menu-end learinig-path-details-card__dropdown"
                  aria-labelledby={menuId}
                  data-bs-popper="static"
                >
                  <li>
                    <button
                      type="button"
                      className="dropdown-item bold mb-0 font_14"
                      onClick={handleShareCourse}
                    >
                      <span
                        className="fas fa-share-alt fa-fw me-2"
                        aria-hidden="true"
                      ></span>
                      {t("learningPaths:shareCourse")}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="dropdown-item bold mb-0 font_14"
                      onClick={handleWatchAgain}
                    >
                      <span
                        className="fas fa-play fa-fw me-2"
                        aria-hidden="true"
                      ></span>
                      {t("learningPaths:watchAgain")}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <Link
            className={`btn ${courseStatusInfo.buttonClass}`}
            to={coursePath}
            state={state}
          >
            <span
              className={`${courseStatusInfo.ctaIcon} icon fa-fw`}
            ></span>
            <div className="flex-auto mb-0">{courseStatusInfo.ctaText}</div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default LearningPathdetailsCard;

LearningPathdetailsCard.prototype = {
  courseStatusClassName: PropTypes.string,
};

LearningPathdetailsCard.deafultProps = {
  courseStatusClassName: "",
};
